import type { Store } from "@reduxjs/toolkit";
import { areConfigurationsEqual } from "../../utilities/configurationComparer.ts";
import { selectLiveConfigurationVersion } from "../selectors/editingHistorySelectors.ts";
import type { ConfigurationVersion } from "../types/editingHistoryState.types.ts";
import type { RootState } from "../types/store.types.ts";
import { createEditingHistoryAction } from "./storeUtils.ts";

const areVersionsEqual = (a: ConfigurationVersion, b: ConfigurationVersion): boolean =>
  a.reportType === b.reportType &&
  a.dataSetId === b.dataSetId &&
  areConfigurationsEqual(a.configuration, b.configuration);

export const observeEditingHistory = (
  store: Pick<Store<RootState>, "getState" | "dispatch" | "subscribe">
): (() => void) => {
  let scheduled = false;
  let observedFields = store.getState().fieldsState;
  let observedDataSetId = store.getState().metaData.dataSetId;

  const recordChange = () => {
    scheduled = false;
    const state = store.getState();
    const { fieldsState } = state;
    const { dataSetId } = state.metaData;
    if (state.uiSettings.editDisabled || (fieldsState === observedFields && dataSetId === observedDataSetId)) {
      return;
    }

    observedFields = fieldsState;
    observedDataSetId = dataSetId;
    const version = selectLiveConfigurationVersion(state);
    const current = state.editingHistory.versions[state.editingHistory.index];
    if (version && current && !areVersionsEqual(current, version)) {
      store.dispatch(createEditingHistoryAction("recorded", version));
    }
  };

  return store.subscribe(() => {
    // One edit dispatches several actions; compare once they have all been applied.
    if (!scheduled) {
      scheduled = true;
      queueMicrotask(recordChange);
    }
  });
};
